// src/components/SendMessage.tsx
import { useEffect, useState, FormEvent, useCallback, useRef } from "react";
import { PlayerRole, type Role, MAX_CHARS_SENT_PER_TURN } from "../shared/constants";
import { buildPlayerName, buildPlayerCodeName } from "../shared/helper";
import { useTimer } from "../contexts/TimerContext";
import { API_BASE_URL } from "../utils/api";

interface Player {
  player_id: string;
  login_code: string;
  role: Role;
  team_name: string;
  team_code_name: string;
}

interface SendMessageProps {
  gameName: string;
  userRole: Role;
  loginCode: string;
  userTeamName: string; // add the logged-in user's team name
  gameStatus: string | null;
  playerId: string;
  currentTurn: number | null;
  charsSentThisTurn?: number | null; // Optional prop to avoid redundant fetches
  extraDisabled?: boolean;
  extraDisabledMessage?: string;
  timerEnabled?: boolean; // When false, timer-based limitations are disabled
  initialSelectedPlayerId?: string; // Pre-select a player when component opens
  commLevel?: number | null; // Communication level for agency players
}

export default function SendMessage({ gameName, userRole, loginCode, userTeamName, gameStatus, playerId, currentTurn, charsSentThisTurn: charsSentThisTurnProp, extraDisabled = false, extraDisabledMessage, timerEnabled = true, initialSelectedPlayerId, commLevel }: SendMessageProps) {
  const { isPaused, timerSeconds } = useTimer();
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>(initialSelectedPlayerId || "");
  const [message, setMessage] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [charsSentThisTurn, setCharsSentThisTurn] = useState<number>(
    charsSentThisTurnProp !== undefined && charsSentThisTurnProp !== null ? charsSentThisTurnProp : 0
  );
  
  // Track previous turn to only fetch when turn actually changes
  const previousTurnRef = useRef<number | null>(null);

  // Determine the opposite role
  const targetRole: Role = userRole === PlayerRole.AGENCY ? PlayerRole.SPACESHIP : PlayerRole.AGENCY;

  // Fetch players of the opposite role in the same game
  useEffect(() => {
    if (!gameName) return;

    fetch(`${API_BASE_URL}/players/by-game-name?game_name=${encodeURIComponent(gameName)}&role=${targetRole}`)
      .then(res => res.json())
      .then((data) => {
        const playersArray = Array.isArray(data) ? data : [];
        // Sort players alphabetically by the display name that will be shown in the dropdown
        const sortedPlayers = playersArray.sort((a, b) => {
          const nameA = userRole === PlayerRole.AGENCY
            ? buildPlayerName(a.team_name, a.role)
            : buildPlayerCodeName(a.team_code_name, a.role);
          const nameB = userRole === PlayerRole.AGENCY
            ? buildPlayerName(b.team_name, b.role)
            : buildPlayerCodeName(b.team_code_name, b.role);
          return nameA.localeCompare(nameB);
        });
        setPlayers(sortedPlayers);
        // If initialSelectedPlayerId is provided and valid, select it
        if (initialSelectedPlayerId && sortedPlayers.some(p => p.player_id === initialSelectedPlayerId)) {
          setSelectedPlayerId(initialSelectedPlayerId);
        }
      })
      .catch(err => console.error("Failed to fetch players:", err));
  }, [gameName, targetRole, initialSelectedPlayerId, userRole]);

  // Track previous initialSelectedPlayerId to detect when it's cleared
  const prevInitialSelectedPlayerIdRef = useRef<string | undefined>(initialSelectedPlayerId);
  
  // Update selectedPlayerId when initialSelectedPlayerId prop changes
  useEffect(() => {
    if (initialSelectedPlayerId && players.some(p => p.player_id === initialSelectedPlayerId)) {
      setSelectedPlayerId(initialSelectedPlayerId);
    } else if (!initialSelectedPlayerId && prevInitialSelectedPlayerIdRef.current) {
      // Clear selection when initialSelectedPlayerId is removed (e.g., manual open after reply)
      // This only clears if it was previously set (not on initial mount)
      setSelectedPlayerId("");
    }
    prevInitialSelectedPlayerIdRef.current = initialSelectedPlayerId;
  }, [initialSelectedPlayerId, players]);

  // Sync local state when prop changes or when turn changes
  useEffect(() => {
    if (charsSentThisTurnProp !== undefined && charsSentThisTurnProp !== null) {
      setCharsSentThisTurn(charsSentThisTurnProp);
    } else if (currentTurn !== previousTurnRef.current && currentTurn !== null) {
      // Turn changed but prop not provided - reset to 0
      setCharsSentThisTurn(0);
      previousTurnRef.current = currentTurn;
    }
  }, [charsSentThisTurnProp, currentTurn]);

  // Fetch character count for current turn (only if prop not provided and turn changed)
  const fetchCharsSentThisTurn = useCallback(async () => {
    if (!playerId || currentTurn === null) {
      setCharsSentThisTurn(0);
      return;
    }

    // Only fetch if prop not provided
    if (charsSentThisTurnProp !== undefined) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/messages/chars-sent-this-turn?player_id=${encodeURIComponent(playerId)}`);
      if (res.ok) {
        const data = await res.json();
        setCharsSentThisTurn(data.chars_sent || 0);
      } else {
        console.error("Failed to fetch character count");
        setCharsSentThisTurn(0);
      }
    } catch (err) {
      console.error("Error fetching character count:", err);
      setCharsSentThisTurn(0);
    }
  }, [playerId, currentTurn, charsSentThisTurnProp]);

  // Fetch character count only when turn actually changes (not on every render)
  useEffect(() => {
    // If prop is provided, sync with prop and update previousTurnRef
    if (charsSentThisTurnProp !== undefined) {
      if (currentTurn !== previousTurnRef.current && currentTurn !== null) {
        // Turn changed - prop should have the new value, just update ref
        previousTurnRef.current = currentTurn;
      }
      return;
    }

    // Only fetch if turn actually changed
    if (currentTurn !== null && currentTurn !== previousTurnRef.current) {
      previousTurnRef.current = currentTurn;
      fetchCharsSentThisTurn();
    } else if (previousTurnRef.current === null && currentTurn !== null) {
      // First mount with valid turn
      previousTurnRef.current = currentTurn;
      fetchCharsSentThisTurn();
    }
  }, [currentTurn, fetchCharsSentThisTurn, charsSentThisTurnProp]);

  // Listen for WebSocket messages to update character count (without fetching)
  useEffect(() => {
    const handleNewMessage = (event: CustomEvent) => {
      // If WebSocket message includes chars_sent_this_turn, use it
      const detail = event.detail;
      if (detail && typeof detail.chars_sent_this_turn === 'number') {
        setCharsSentThisTurn(detail.chars_sent_this_turn);
        return;
      }
      
      // Otherwise, only fetch if prop not provided (fallback for old messages)
      if (charsSentThisTurnProp === undefined) {
        // Small delay to let backend process the message first
        setTimeout(() => {
          fetchCharsSentThisTurn();
        }, 100);
      }
    };

    window.addEventListener('newMessage', handleNewMessage as EventListener);
    return () => {
      window.removeEventListener('newMessage', handleNewMessage as EventListener);
    };
  }, [fetchCharsSentThisTurn, charsSentThisTurnProp]);

  // Calculate total characters including the current message being typed
  const totalCharsWithCurrent = charsSentThisTurn + message.length;
  const isOverLimit = totalCharsWithCurrent > MAX_CHARS_SENT_PER_TURN;
  const remainingChars = Math.max(0, MAX_CHARS_SENT_PER_TURN - totalCharsWithCurrent);
  const maxMessageLength = Math.max(0, MAX_CHARS_SENT_PER_TURN - charsSentThisTurn);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Prevent submission if game is not active
    if (gameStatus !== "ACTIVE") {
      setStatus("Messages can only be sent when the game is ACTIVE.");
      return;
    }

    if (!selectedPlayerId || !message) {
      setStatus("Please select a player and write a message.");
      return;
    }

    // Check character limit
    if (totalCharsWithCurrent > MAX_CHARS_SENT_PER_TURN) {
      setStatus(`Message exceeds character limit. You can only send ${MAX_CHARS_SENT_PER_TURN} characters per turn.`);
      return;
    }

    // Find the selected player object
    const targetPlayer = players.find(p => p.player_id === selectedPlayerId);
    if (!targetPlayer) {
      setStatus("Selected player not found.");
      return;
    }

    const payload = {
      sending_team: userTeamName,
      sending_player_role: userRole,
      receiving_team: targetPlayer.team_name,
      receiving_player_role: targetPlayer.role,
      message
    };

    fetch(`${API_BASE_URL}/messages/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to send message");
        return res.json();
      })
      .then(() => {
        setStatus("Message sent!");
        // Optimistically update character count by adding message length
        // This avoids a redundant fetch, and WebSocket will sync the actual value
        setCharsSentThisTurn(prev => prev + message.length);
        setMessage("");
        // WebSocket will update with the actual value, so we don't need to fetch
      })
      .catch(err => setStatus(err.message));
  };

  const isTimerZero = (timerSeconds ?? 0) === 0;
  // Timer-based limitations only apply if timer_enabled is true
  const timerLimitation = timerEnabled && (isPaused || isTimerZero);
  const isDisabled = gameStatus !== "ACTIVE" || extraDisabled || timerLimitation;
  // Disable send button if:
  // 1. Component is disabled (game not active, player eliminated, timer paused/zero, etc.)
  // 2. Over character limit
  // 3. Already sent max characters AND input is empty
  const isSendDisabled = isDisabled || isOverLimit || (charsSentThisTurn === MAX_CHARS_SENT_PER_TURN && message.length === 0);

  return (
    <div style={{
      border: "1px solid #dee2e6", 
      padding: isCollapsed ? "0.5rem 1rem" : "1rem", 
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      opacity: isDisabled ? 0.6 : 1,
      width: "100%",
      display: "flex",
      flexDirection: "column"
    }}>
      <h2 style={{ margin: 0, color: "#212529", fontSize: "1.1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <span>
          Send Message{userRole === PlayerRole.AGENCY && commLevel !== null && commLevel !== undefined ? ` (Communication level: ${commLevel})` : ''}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <span style={{
            padding: "0.5rem",
            backgroundColor: isOverLimit ? "#f8d7da" : "#d1ecf1",
            color: isOverLimit ? "#721c24" : "#0c5460",
            borderRadius: "4px",
            border: `1px solid ${isOverLimit ? "#f5c6cb" : "#bee5eb"}`,
            fontSize: "0.85rem"
          }}>
            <strong>Characters used this turn:</strong> {charsSentThisTurn} / {MAX_CHARS_SENT_PER_TURN}
            {message.length > 0 && (
              <span style={{ marginLeft: "0.5rem" }}>
                (with current message: <strong style={{ color: isOverLimit ? "#dc3545" : "inherit" }}>
                  {totalCharsWithCurrent}
                </strong>)
              </span>
            )}
            {userRole !== PlayerRole.AGENCY && remainingChars >= 0 && remainingChars < MAX_CHARS_SENT_PER_TURN && (
              <span style={{ marginLeft: "0.5rem", color: "#6c757d" }}>
                ({remainingChars} remaining)
              </span>
            )}
            {isOverLimit && (
              <div style={{ marginTop: "0.5rem", fontWeight: "bold" }}>
                Character limit exceeded! Please shorten your message.
              </div>
            )}
          </span>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "1.2rem",
              color: "#6c757d",
              padding: "0.25rem 0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            title={isCollapsed ? "Expand" : "Collapse"}
          >
            {isCollapsed ? "▼" : "▲"}
          </button>
        </div>
      </h2>
      
      {!isCollapsed && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "1rem" }}>

        {(isDisabled || isOverLimit) && (
          <div style={{
            padding: "0.5rem",
            backgroundColor: isOverLimit ? "#f8d7da" : "#fff3cd",
            color: isOverLimit ? "#721c24" : "#856404",
            borderRadius: "4px",
            border: `1px solid ${isOverLimit ? "#f5c6cb" : "#ffc107"}`,
            fontSize: "0.85rem"
          }}>
            <strong>Message sending is disabled.</strong> {
              isOverLimit 
                ? `You have reached the character limit of ${MAX_CHARS_SENT_PER_TURN} characters per turn. Please shorten your message.`
                : extraDisabled && extraDisabledMessage 
                  ? extraDisabledMessage 
                  : "Messages can only be sent when the game status is ACTIVE and there is time left on the timer."
            }
            {gameStatus && !extraDisabled && !isOverLimit && (
              <span> Current status: <strong>{gameStatus}</strong></span>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <select 
          value={selectedPlayerId} 
          onChange={e => setSelectedPlayerId(e.target.value)}
          disabled={isDisabled}
          style={{ 
            width: "100%",
            padding: "0.5rem",
            fontSize: "0.95rem",
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.6 : 1,
            backgroundColor: isDisabled ? "#f8f9fa" : "white"
          }}
        >
          <option value="">-- Select a player --</option>
          {players.map(p => (
            <option key={p.player_id} value={p.player_id}>
              {userRole === PlayerRole.AGENCY
                ? buildPlayerName(p.team_name, p.role)
                : buildPlayerCodeName(p.team_code_name, p.role)}
            </option>
          ))}
        </select>

        <textarea
            value={message}
            onChange={e => {
              const newValue = e.target.value;
              // Only allow Latin characters, symbols, and basic punctuation
              // Regex: allows a-z, A-Z, 0-9, and common symbols/punctuation
              const latinAndSymbolsPattern = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/;
              const isValid = latinAndSymbolsPattern.test(newValue);
              
              // Only allow typing if it doesn't exceed the remaining character limit and contains only allowed characters
              if (newValue.length <= maxMessageLength && isValid) {
                setMessage(newValue);
              }
            }}
            placeholder="Write your message (English, numbers and symbols only)"
            rows={2}
            disabled={isDisabled}
            style={{ 
              width: "97.5%",
              padding: "0.5rem",
              fontSize: "0.95rem",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              fontFamily: "inherit",
              resize: "vertical",
              cursor: isDisabled ? "not-allowed" : "text",
              opacity: isDisabled ? 0.6 : 1,
              backgroundColor: isDisabled ? "#f8f9fa" : "white"
            }}
          />

        <button 
          type="submit" 
          disabled={isSendDisabled}
          style={{
            padding: "0.5rem 1rem",
            fontSize: "0.9rem",
            cursor: isSendDisabled ? "not-allowed" : "pointer",
            backgroundColor: isSendDisabled ? "#6c757d" : "#007bff",
            color: "white",
            border: "none",
          borderRadius: "4px",
          fontWeight: "bold",
          opacity: isSendDisabled ? 0.6 : 1
          }}
        >
          Send
        </button>
      </form>
        
        {status && (
          <div style={{
            padding: "0.5rem",
            backgroundColor: status.includes("sent") || status.includes("!") ? "#d4edda" : "#f8d7da",
            color: status.includes("sent") || status.includes("!") ? "#155724" : "#721c24",
            borderRadius: "4px",
            border: `1px solid ${status.includes("sent") || status.includes("!") ? "#c3e6cb" : "#f5c6cb"}`,
            fontSize: "0.85rem"
          }}>
            {status}
          </div>
        )}
        </div>
      )}
    </div>
  );
}
